import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type {
    RecordTaxChangeInput,
    RecordTaxChangeResponse,
} from '@/api/handlers/resources/settings';
import type { ApiResponse } from '@/api/handlers/responses';
import { AccountQueryKeys } from '../accounts/accountQueryKeys';
import { DividendQueryKeys } from '../dividends/dividendQueryKeys';
import { SalaryQueryKeys } from '../salaries/salaryQueryKeys';
import { StatementQueryKeys } from '../statements/statementQueryKeys';
import { SettingsQueryKeys } from './settingsQueryKeys';

/**
 * What a rate change moves — which is, by design, **everything that reads a rate**.
 *
 * This is the "changing a rate must invalidate any query that depends on it"
 * requirement discharged in one place. Enumerated explicitly rather than clearing
 * the whole cache, so the list is a readable statement of which domains are
 * rate-dependent — and so adding a new one is a deliberate edit here rather than
 * something that silently works until it doesn't.
 *
 * - **settings** — the rates themselves.
 * - **salaries** — a payroll preview computed under the previous PIT / pension
 *   rates is now wrong; the register summary's employer cost moves with the
 *   employer pension rate.
 * - **dividends** — the distribution calculator, the summary's CIT/WHT figures and
 *   any un-declared preview are all rate-derived.
 * - **statements** and **accounts** — untouched by a rate change *today*, since
 *   nothing accrues tax outside a posted entry. Included because the cost is a
 *   refetch of two cached queries, and the failure mode if a future rate-driven
 *   accrual lands and this list was not updated is a stale balance on screen.
 */
export const invalidateAfterSettingsWrite = (
    queryClient: QueryClient,
): void => {
    queryClient.invalidateQueries({ queryKey: SettingsQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: SalaryQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: DividendQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: StatementQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
};

/**
 * Record a tax rate change.
 *
 * The write is an append, so a success invalidates the history and every cached
 * date lookup as well as the current set — a new set can change what any date
 * on or after its `effectiveFrom` resolves to.
 */
export const useRecordTaxChangeMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<RecordTaxChangeResponse>,
        Error,
        RecordTaxChangeInput
    >({
        mutationFn: (body) => apiClient.settings.recordTaxChange(body),
        onSuccess: (result) => {
            if (result.success) {
                invalidateAfterSettingsWrite(queryClient);
            }
        },
    });
};
